/* ============================================================================
 * VELO — Assortment variant picker (reliable / minimal)
 * Goal: no "N/A", and hide an option row entirely when there's no real choice —
 * WITHOUT rebuilding the native <select> menus (rebuilding them caused a scroll
 * jump and cross-device flakiness).
 *
 * Decisions are computed from the product's own variant data (deterministic and
 * synchronous — the theme's `disabled` states update async and gave wrong
 * answers mid-transition). But we only ever:
 *   1) hide the whole option row (label + control) when it has <=1 real choice
 *      (Color for Gift Bag / 5-lb Box; Size for the single-size 5-lb Box);
 *   2) remove ONLY the placeholder "N/A" option from the Color dropdown when
 *      real colors apply (re-adding it when it's the actual value, row hidden);
 *   3) set a valid value when the current pick becomes invalid, so price /
 *      Add-to-Cart stay live.
 * We never rebuild the Size/Color option lists, so genuinely unavailable
 * sizes/colors simply keep the theme's native greying. No option-list churn =
 * no scroll jump. Scoped to whatever template references this file.
 * ========================================================================== */
(function () {
  'use strict';

  function isNA(v) { return (v || '').trim().toUpperCase() === 'N/A'; }
  function outer(sel) { return sel.closest('.variant-wrapper') || sel.closest('.variant-input-wrap'); }
  function selIndex(sel) {
    var w = sel.closest('.variant-input-wrap');
    var di = (w && w.getAttribute('data-index')) || sel.getAttribute('data-index') || '';
    var m = di.match(/(\d+)/);
    return m ? (parseInt(m[1], 10) - 1) : 0;
  }
  function selects() {
    return Array.prototype.slice.call(document.querySelectorAll('.variant-input-wrap select'))
      .sort(function (a, b) { return selIndex(a) - selIndex(b); });
  }
  function opts(sel) { return Array.prototype.slice.call(sel.options); }
  function hasValue(sel, val) { return opts(sel).some(function (o) { return o.value === val; }); }

  var VARIANTS = null;   // [{options:[o1,o2,o3], available:bool}]
  var naRef = null;      // detached "N/A" <option> for the color select, if removed
  var busy = false;

  // --- Suppress the theme's gallery scroll on variant change -----------------
  // On a variant change the theme switches the featured image and calls
  // activeThumb.scrollIntoView(...) (again on the image's height `transitionend`),
  // which yanks the whole page on desktop. Rather than let it scroll and snap it
  // back (that flashes), we SUPPRESS scrollIntoView for a short window around the
  // change so the page never moves. Armed in the capture phase so it beats the
  // theme's own change handler, which fires the scroll synchronously.
  var suppressUntil = 0;
  function armSuppress() { suppressUntil = Date.now() + 700; }
  (function patchScrollIntoView() {
    var proto = Element.prototype;
    if (proto.__veloSIVPatched) return;
    proto.__veloSIVPatched = true;
    var orig = proto.scrollIntoView;
    proto.scrollIntoView = function () {
      if (Date.now() < suppressUntil) return;   // swallow the theme's gallery scroll
      return orig.apply(this, arguments);
    };
  })();

  // Values for option `idx` that appear in an available variant matching the
  // already-chosen values of the options to its LEFT. May include "N/A".
  function validValues(idx, chosen) {
    var set = [];
    VARIANTS.forEach(function (v) {
      if (v.available === false) return;
      for (var j = 0; j < idx; j++) {
        if (chosen[j] != null && v.options[j] !== chosen[j]) return;
      }
      var val = v.options[idx];
      if (set.indexOf(val) === -1) set.push(val);
    });
    return set;
  }

  function sync() {
    if (busy || !VARIANTS) return;
    busy = true;
    var sels = selects();
    var chosen = [];
    sels.forEach(function (s) { chosen[selIndex(s)] = s.value; });

    var changed = false;
    sels.forEach(function (sel) {
      var idx = selIndex(sel);
      var valid = validValues(idx, chosen);                    // available values (may include N/A)
      var real = valid.filter(function (v) { return !isNA(v); });
      var pool = real.length ? real : valid;                   // what the value is allowed to be

      // Correct an invalid current selection (no option-list rebuild — the target
      // option element already exists in the native <select>).
      if (pool.indexOf(sel.value) === -1 && pool.length) { sel.value = pool[0]; changed = true; }
      chosen[idx] = sel.value;                                 // feed corrected value to options on the right

      // Remove / restore only the "N/A" placeholder option (color).
      var na = opts(sel).filter(function (o) { return isNA(o.value); })[0];
      if (real.length >= 1) {
        if (na) { naRef = na; na.parentNode.removeChild(na); }
      } else if (!na && naRef && isNA(pool[0])) {
        sel.appendChild(naRef);
      }

      // Hide the whole option row when there's no real choice.
      var box = outer(sel);
      if (box) box.style.display = (real.length <= 1) ? 'none' : '';
    });

    busy = false;
    if (changed && sels.length) {
      sels[0].dispatchEvent(new Event('change', { bubbles: true })); // update price / variant / ATC
    }
  }

  function init() {
    if (document._veloVariantClean) return;
    if (!document.querySelector('.variant-input-wrap select')) return;
    document._veloVariantClean = true;

    var handle = (location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
    if (!handle) return;

    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        VARIANTS = (p.variants || []).map(function (v) { return { options: v.options, available: v.available }; });
        sync();
        // Arm scroll-suppression in the CAPTURE phase so it runs before the
        // theme's own change handler (which fires the gallery scroll synchronously).
        document.addEventListener('pointerdown', function (e) {
          if (e.target.closest && e.target.closest('.variant-input-wrap')) armSuppress();
        }, true);
        document.addEventListener('change', function (e) {
          if (e.target.closest && e.target.closest('.variant-input-wrap')) armSuppress();
        }, true);
        selects().forEach(function (s) {
          s.addEventListener('change', function () {
            if (busy) return;
            setTimeout(sync, 0);
          });
        });
      })
      .catch(function () { /* leave the native picker untouched on failure */ });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
